// Mirrors plutus-services scopeRegistry.ts at 0505db90; these are saved grants, not token permissions.
export const USER_PERMISSION_GROUPS = [
  { role: "INVESTOR", label: "Investor", scopes: [
    { value: "investor:read", label: "Read investor data" },
    { value: "investor:*", label: "Investor access" },
  ] },
  { role: "PROVIDER", label: "Provider", scopes: [
    { value: "provider:read", label: "Read provider data" },
    { value: "provider:*", label: "Provider access" },
  ] },
  { role: "ADVISOR", label: "Advisor", scopes: [
    { value: "advisor:read", label: "Read advisor data" },
    { value: "advisor:*", label: "Advisor access" },
  ] },
  { role: "ADMIN", label: "Admin", scopes: [
    { value: "admin:read", label: "Read all administration" },
    { value: "admin:product-read", label: "Read products" },
    { value: "admin:product-write", label: "Manage products" },
    { value: "admin:contract-read", label: "Read contracts" },
    { value: "admin:contract-write", label: "Manage contracts" },
    { value: "admin:user-read", label: "Read users" },
    { value: "admin:user-write", label: "Manage ordinary user data" },
    { value: "admin:*", label: "All administration" },
  ] },
] as const;

export function scopesForRole(role: string): readonly string[] {
  return USER_PERMISSION_GROUPS.find(group => group.role === role)?.scopes.map(scope => scope.value) ?? [];
}

export function validateUserPermissions(roles: readonly string[], scopes: readonly string[]): string | null {
  if (roles.length === 0) return "Select at least one role.";
  if (new Set(roles).size !== roles.length || new Set(scopes).size !== scopes.length) {
    return "Roles and scopes cannot contain duplicates.";
  }
  if (roles.some(role => scopesForRole(role).length === 0)) return "A selected role is not supported.";
  if (scopes.some(scope => !roles.some(role => scopesForRole(role).includes(scope)))) {
    return "Every scope must belong to a selected role.";
  }
  const missing = USER_PERMISSION_GROUPS.filter(group => roles.includes(group.role)
    && !group.scopes.some(scope => scopes.includes(scope.value)));
  return missing.length ? `Select at least one scope for ${missing.map(group => group.label).join(", ")}.` : null;
}

export function samePermissionSet(first: readonly string[], second: readonly string[]): boolean {
  const sortedSecond = [...second].sort();
  return first.length === second.length && [...first].sort().every((value, index) => value === sortedSecond[index]);
}

/** UI affordance only. The backend verifies the token, current actor and admin:* on every save. */
export function canManageUserPermissions(
  actor: { id: string; roles?: readonly string[] } | null,
  token: string | null,
  now = Date.now(),
): boolean {
  if (!actor?.roles?.includes("ADMIN") || !token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const encoded = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims: unknown = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=")));
    if (!claims || typeof claims !== "object") return false;
    const payload = claims as Record<string, unknown>;
    return payload.userId === actor.id && Array.isArray(payload.roles) && payload.roles.includes("ADMIN")
      && typeof payload.scope === "string" && payload.scope.split(/\s+/).includes("admin:*")
      && typeof payload.exp === "number" && Number.isFinite(payload.exp) && payload.exp * 1000 > now;
  } catch {
    return false;
  }
}
